import { useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PageShell } from "@/components/site/Site";
import { api, errMsg, SUPPORT_EMAIL } from "@/lib/api";

export default function Contact() {
  const started = useRef(Date.now() / 1000);
  const [f, setF] = useState({ name: "", email: "", subject: "General enquiry", message: "", website: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { ...f, started_at: started.current });
      setSent(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell>
      <section className="mx-auto grid max-w-6xl gap-16 px-5 py-20 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <h1 className="font-serif-display text-4xl sm:text-5xl">Contact, Complaints & Feedback</h1>
          <p className="mt-6 text-stone-600">We read every message. For anything about your order or report, include your order reference.</p>
          <a href={`mailto:${SUPPORT_EMAIL}`} className="mt-8 block font-serif-display text-2xl text-[#5B2C36] underline decoration-[#B8860B]/40 underline-offset-8" data-testid="contact-email-link">{SUPPORT_EMAIL}</a>
        </div>
        {sent ? (
          <div className="border border-[#E6DEC8] bg-white p-10 lg:col-span-6 lg:col-start-7" data-testid="contact-success">
            <h2 className="font-serif-display text-3xl">Thank you.</h2>
            <p className="mt-3 text-stone-600">Your message has been received. We'll reply to {f.email}.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5 border border-[#E6DEC8] bg-white p-8 sm:p-10 lg:col-span-6 lg:col-start-7" data-testid="contact-form">
            <div><Label>Your name</Label><Input required value={f.name} onChange={set("name")} className="mt-2" data-testid="contact-name-input" /></div>
            <div><Label>Email</Label><Input required type="email" value={f.email} onChange={set("email")} className="mt-2" data-testid="contact-email-input" /></div>
            <div>
              <Label>Subject</Label>
              <select value={f.subject} onChange={set("subject")} className="mt-2 h-10 w-full rounded-sm border border-input bg-transparent px-3 text-sm" data-testid="contact-subject-select">
                {["General enquiry", "Order or report", "Complaint", "Feedback"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div><Label>Message</Label><Textarea required minLength={5} rows={6} value={f.message} onChange={set("message")} className="mt-2" data-testid="contact-message-input" /></div>
            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" data-testid="contact-honeypot" />
            <button disabled={busy} className="rounded-full bg-stone-900 px-7 py-3 text-sm text-white transition-colors hover:bg-[#5B2C36] disabled:opacity-50" data-testid="contact-submit-btn">
              {busy ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </section>
    </PageShell>
  );
}
